import React, { useEffect, useRef, useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useNavigationType,
  useOutlet,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { I18nextProvider } from "react-i18next";
import i18n, { initLanguage } from "@/i18n";
import { App } from "@capacitor/app";
import Layout from "@/components/Layout";
import HomePage from "@/pages/Home/HomePage";
import CartPage from "@/pages/Cart/CartPage";
import AddressesPage from "@/pages/Addresses/AddressesPage";
import LoginPage from "@/pages/Login/LoginPage";
import ProfilePage from "@/pages/Profile/ProfilePage";
import AdvancedPage from "@/pages/Profile/AdvancedPage";
import SelectLanguagePage from "@/pages/SelectLanguge/SelectLanguagePage";
import ProductDetailsPage from "@/pages/ProductDetails/ProductDetailsPage";
import ProductsPageV2 from "@/pages/ProductsPage/ProductsPageV2";
import OrdersPage from "@/pages/Orders/OrdersPage";
import OrderDetailsPage from "@/pages/Orders/OrderDetailsPage";
import OnboardingPage from "@/pages/Onboarding/OnboardingPage";
import OrderSuccessPage from "@/pages/OrderSuccess/OrderSuccessPage";
import TermsAndConditionsPage from "@/pages/Legal/TermsAndConditionsPage";
import PrivacyPolicyPage from "@/pages/Legal/PrivacyPolicyPage";
import ReturnRefundPolicyPage from "@/pages/Legal/ReturnRefundPolicyPage";
import AboutPage from "@/pages/Legal/AboutPage";
import useUser from "@/hooks/useUser";
import Spinner from "@/components/Spinner";
import useGpsLocation from "@/hooks/useGpsLocation";
import { useDispatch, useSelector } from "react-redux";
import { setGpsLocation } from "@/store/slices/userSlice";
import useApi from "@/utils/useApi";
import apis from "@/utils/apis";
import LocationGateModal from "@/components/LocationGateModal";


const isAuthenticated = () => {
  return !!localStorage.getItem("token");
};

const isLocationSet = () => {
  const defaultLocationId = localStorage.getItem("defaultLocationId");
  return defaultLocationId !== null && defaultLocationId !== "undefined" && defaultLocationId !== "null";
};

const isLanguageSet = () => {
  const language = localStorage.getItem("language");
  // Only return true if language is explicitly set by the user
  return language !== null && language !== undefined;
};

const LanguageRoute = () => {
  if (!isLanguageSet()) {
    return <Navigate to="/select-language" replace />;
  }
  return <Outlet />;
};




const PrivateRoute = () => {
  const { userInfo } = useUser();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (!userInfo) {
    return <Spinner />;
  }

  return <Outlet />;
};

// Page animation variants
const pageVariants = {
  initial: {
    x: 0,
    opacity: 0,
  },
  in: {
    opacity: 1,
    transition: {
      type: "tween",
      ease: "easeInOut",
      duration: 0.2,
    },
  },
  out: (direction) => ({
    x: direction > 0 ? "-50%" : "50%",
    opacity: 0,
    transition: {
      type: "tween",
      ease: "easeInOut",
      duration: 0.15,
    },
  }),
};

// AnimatedOutlet component for wrapping animated routes
const AnimatedOutlet = () => {
  const o = useOutlet();
  const [outlet] = React.useState(o);
  return <>{outlet}</>;
};

// AnimatedRoutes for handling page transitions
const AnimatedRoutes = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const direction = navigationType === "POP" ? -1 : 1;
  const savedScrollRef = useRef({});

  // ── Deep link handler ──────────────────────────────────────────────────────
  useEffect(() => {
    let listener;
    App.addListener("appUrlOpen", (event) => {
      try {
        const url = new URL(event.url);
        // Handles both:
        //   kiranapoint://profile/advanced  → pathname = /profile/advanced
        //   https://app.kiranapoint.in/orders/123 → pathname = /orders/123
        const path = url.pathname;
        if (path && path !== "/") {
          navigate(path, { replace: false });
        }
      } catch {
        // ignore malformed URLs
      }
    }).then((l) => { listener = l; });
    return () => { listener?.remove(); };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Location Gate state ────────────────────────────────────────────────────
  // Open on app mount when authenticated; dismissable if location already set
  const [gateOpen, setGateOpen] = useState(isAuthenticated());
  const [locationChanged, setLocationChanged] = useState(false);
  const [gateIsDismissable, setGateIsDismissable] = useState(isAuthenticated() && isLocationSet());
  const martResumeChecked = useRef(false);
  // Track auth state so we only re-open gate when user actually logs in (not on nav)
  const prevAuthRef = useRef(isAuthenticated());
  const { getLocation } = useGpsLocation();
  const { callApi: callNearestNearbyApi } = useApi();
  const userInfo = useSelector((state) => state?.user?.userInfo);
  const dispatch = useDispatch();

  // Only reopen gate when user transitions from logged-out → logged-in
  useEffect(() => {
    const nowAuth = isAuthenticated();
    if (nowAuth && !prevAuthRef.current) {
      setGateOpen(true);
      setGateIsDismissable(isLocationSet());
    }
    prevAuthRef.current = nowAuth;
  }, [userInfo]);

  // Detect location change on app resume (once per session)
  useEffect(() => {
    let listener;
    const setup = async () => {
      listener = await App.addListener("appStateChange", async ({ isActive }) => {
        if (!isActive || martResumeChecked.current) return;
        if (!isAuthenticated() || !isLocationSet()) return;
        martResumeChecked.current = true;

        try {
          const coords = await getLocation();
          if (!coords) return;
          dispatch(setGpsLocation(coords));

          const result = await callNearestNearbyApi({
            api: {
              url: `${apis.getNearestNearbyLocation.url}?latitude=${coords.latitude}&longitude=${coords.longitude}`,
              method: apis.getNearestNearbyLocation.method,
            },
          });

          const currentNearbyId =
            userInfo?.defaultAddress?.nearby?._id ||
            userInfo?.defaultAddress?.nearby;

          if (result?.found && result.nearbyLocation?._id !== currentNearbyId) {
            setLocationChanged(true);
            setGateIsDismissable(true);
            setGateOpen(true);
          }
        } catch {
          // silent — resume check is best-effort
        }
      });
    };
    setup();
    return () => {
      if (listener) listener.remove();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Save scroll position before navigating away
    return () => {
      savedScrollRef.current[location.key] = window.scrollY;
    };
  }, [location]);

  useEffect(() => {
    if (navigationType === "POP") {
      // On back navigation, restore scroll position
      const targetScroll = savedScrollRef.current[location.key];
      
      if (targetScroll !== undefined) {
        // Restore immediately
        requestAnimationFrame(() => {
          window.scrollTo(0, targetScroll);
        });
        
        // Retry after animation completes
        setTimeout(() => {
          window.scrollTo(0, targetScroll);
        }, 300);
        
        // Final attempt after content loads
        setTimeout(() => {
          window.scrollTo(0, targetScroll);
        }, 600);
      }
    } else {
      // Scroll to top on forward navigation
      window.scrollTo(0, 0);
    }
  }, [location.key, navigationType]);

  return (
    <>
      {/* Location gate modal — lives outside AnimatePresence so it never unmounts */}
      <LocationGateModal
        isOpen={gateOpen}
        locationChanged={locationChanged}
        isDismissable={gateIsDismissable || locationChanged}
        onSuccess={() => {
          setGateOpen(false);
          setLocationChanged(false);
          setGateIsDismissable(false);
        }}
      />
      <AnimatePresence initial={false} mode="wait" custom={direction}>
        <motion.div
          key={location.pathname}
          custom={direction}
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
          className="grow flex flex-col"
        >
          <AnimatedOutlet />
        </motion.div>
      </AnimatePresence>
    </>
  );
};

// Router configuration
const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        path: "/",
        element: <AnimatedRoutes />,
        children: [
          {
            path: "/",
            element: <LanguageRoute />, // Check language first
            children: [
              {
                path: "/",
                element: <PrivateRoute />,
                children: [
                  {
                    index: true,
                    element: <HomePage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/cart",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />, // Protects the route
                children: [
                  {
                    index: true,
                    element: <CartPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/order-success",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />, // Protects the route
                children: [
                  {
                    index: true,
                    element: <OrderSuccessPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/orders",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />, // Protects the route
                children: [
                  {
                    index: true,
                    element: <OrdersPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/orders/:orderId",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />, // Protects the route
                children: [
                  {
                    index: true,
                    element: <OrderDetailsPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/addresses",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />, // Protects the route
                children: [
                  {
                    index: true,
                    element: <AddressesPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/login",
            element: <LanguageRoute />,
            children: [
              {
                index: true,
                element: <LoginPage />,
              },
            ],
          },
          {
            path: "/profile",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />, // Protects the route
                children: [
                  {
                    index: true,
                    element: <ProfilePage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/profile/advanced",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />,
                children: [
                  {
                    index: true,
                    element: <AdvancedPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "/products/v2/:categoryId?",
            element: <LanguageRoute />,
            children: [
              {
                path: "",
                element: <PrivateRoute />,
                children: [
                  {
                    index: true,
                    element: <ProductsPageV2 />,
                  },
                ],
              },
            ],
          },
          {
            path: "/select-language",
            element: <SelectLanguagePage />, // No protection for language selection
          },
          {
            path: "/terms",
            element: <TermsAndConditionsPage />,
          },
          {
            path: "/privacy",
            element: <PrivacyPolicyPage />,
          },
          {
            path: "/refund-policy",
            element: <ReturnRefundPolicyPage />,
          },
          {
            path: "/about",
            element: <AboutPage />,
          },
          {
            path: "/product/:productId",
            element: <LanguageRoute />,
            children: [
              {
                index: true,
                element: <ProductDetailsPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/onboarding",
    element: <OnboardingPage />,
  },
]);

// Routes component
const Routes = () => {
  // Initialize language from localStorage if it exists
  useEffect(() => {
    initLanguage();
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <RouterProvider router={router} />
    </I18nextProvider>
  );
};

export default Routes;
